/**
 * Leesbare Nederlandse regels voor het contact-activiteitenlog (audit_log).
 */

export type ContactAuditTimelineRow = {
  id: number;
  created_at: string;
  user_id: number | null;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  detail: Record<string, unknown> | null;
  ip: string | null;
  user_name: string | null;
  user_email: string | null;
  group_name: string | null;
};

const KIND_NL: Record<string, string> = {
  personeel: "personeel",
  vrijwilliger: "vrijwilligers",
  dominicusganger: "dominicusgangers",
  extern: "externe contacten",
  leverancier: "leveranciers",
};

function num(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Korte zin voor op de kaart; `contactId` = de geopende kaart. */
export function summarizeContactAuditRow(row: ContactAuditTimelineRow, contactId: number): string {
  const d = row.detail ?? {};
  const g = row.group_name?.trim() || (row.entity_id != null ? `groep #${row.entity_id}` : "een groep");
  const nameHint = typeof d.name === "string" && d.name.trim() ? ` (${d.name.trim()})` : "";

  switch (row.action) {
    case "group.member.add": {
      const via = typeof d.via === "string" ? d.via : "";
      let extra = "";
      if (via === "group_member_add_request") extra = " (na goedkeuring van een lidmaatschapsverzoek)";
      else if (via === "werkgroep_intake_approve") extra = " (na goedkeuring werkgroep-ledenlijst door secretaris/beheer)";
      return `Toegevoegd aan groep “${g}”${extra}.`;
    }
    case "group.member.remove":
      return `Verwijderd uit groep “${g}”.`;
    case "group.member_add_request":
      return `Verzoek ingediend om als lid toegevoegd te worden aan “${g}” (secretaris/groepsbeheer moet nog akkoord gaan).`;
    case "group.member_detail_request":
      return `Verzoek om aanvullende gegevens voor “${g}”.`;
    case "group.member_detail_resolve": {
      const res = String(d.resolution ?? "");
      if (res === "approved") return `Het verzoek om jouw gegevens voor “${g}” is goedgekeurd.`;
      if (res === "rejected") return `Het verzoek om jouw gegevens voor “${g}” is afgewezen.`;
      return `Het verzoek om jouw gegevens voor “${g}” is afgehandeld (geen akkoord/afkeur).`;
    }
    case "group.member_add_request_resolve": {
      const res = String(d.resolution ?? "");
      if (res === "approved" && d.member_added === true) {
        return `Je lidmaatschapsverzoek voor “${g}” is goedgekeurd (je bent toegevoegd als lid).`;
      }
      if (res === "rejected") return `Je lidmaatschapsverzoek voor “${g}” is afgewezen.`;
      return `Je lidmaatschapsverzoek voor “${g}” is afgehandeld.`;
    }
    case "group.share_email":
      return `Groepsmail verstuurd: jouw gegevens uit “${g}” zaten in de gedeelde lijst bij de e-mail.`;
    case "group.chair_set":
      return `Je bent voorzitter geworden van groep “${g}”.`;
    case "group.chair_remove":
      return `Je bent niet meer voorzitter van groep “${g}” (voorzitter verwijderd).`;
    case "group.admin_add":
      return `Je bent groepsbeheerder geworden van “${g}”.`;
    case "group.admin_remove":
      return `Je bent geen groepsbeheerder meer van “${g}”.`;
    case "group.viewer_add":
      return `Je bent groepskijker geworden van “${g}”.`;
    case "group.viewer_remove":
      return `Je bent geen groepskijker meer van “${g}”.`;
    case "group.member_taken_add":
      return `Taak toegevoegd voor jou in groep “${g}”.`;
    case "group.member_taken_remove":
      return `Taak verwijderd voor jou in groep “${g}”.`;
    case "group.chair_demote_group_admin":
      return `Voorzitter-wijziging in “${g}” (groepsbeheer opnieuw ingericht).`;
    case "contact.label_add":
      return `Vrije tag toegevoegd: “${String(d.label ?? "")}”.`;
    case "contact.label_remove":
      return `Vrije tag verwijderd: “${String(d.label ?? "")}”.`;
    case "contact.relation_add": {
      const rc = String(d.relation_code ?? "");
      const from = num(d.mirrored_from_contact_id);
      const rev = d.reverse_relation_code != null ? String(d.reverse_relation_code) : "";
      if (from != null) {
        return `Automatisch: op jouw kaart staat nu “${rc}” t.o.v. kaart #${num(d.other_contact_id) ?? "?"}${
          rev ? ` (zij hadden “${rev}” gekozen)` : ""
        }.`;
      }
      return `Relatie toegevoegd (${rc}) met kaart #${num(d.other_contact_id) ?? "?"}${
        rev ? `; op hun kaart: ${rev}` : ""
      }.`;
    }
    case "contact.relation_remove":
      return `Relatie verwijderd (regel #${num(d.relation_row_id) ?? "?"}).`;
    case "contact.relation_update": {
      const fr = String(d.from ?? "");
      const to = String(d.to ?? "");
      return `Partnerkoppeling gewijzigd: ${fr || "—"} → ${to || "—"} (met kaart #${num(d.other_contact_id) ?? "?"}).`;
    }
    case "admin.contact_app_role":
      return `App-rol op de kaart gezet: ${String(d.app_role ?? "—")}.`;
    case "admin.user_link":
      return `Koppeling tussen Microsoft-account en deze kaart aangepast.`;
    case "admin.vrijwilliger_demote_to_dominicusganger": {
      const mode = typeof d.mode === "string" ? d.mode : "";
      const trigger = typeof d.trigger === "string" ? d.trigger : "";
      const autoSuffix =
        trigger === "group.member.remove"
          ? " (automatisch na verwijdering als groepslid, geen andere groepsrollen meer)"
          : trigger === "group.chair_remove"
            ? " (automatisch na afzetten als voorzitter, geen andere groepsrollen meer)"
            : trigger === "group.delete"
              ? " (automatisch na verwijderen van de groep, geen andere groepsrollen meer)"
              : "";
      if (mode === "removed_vrijwilliger_label_only") {
        return `Alleen het label “vrijwilliger” verwijderd (primair type bleef ${String(d.entity_kind_after ?? "—")}).${autoSuffix}`;
      }
      if (mode === "labels_to_primary_dominicusganger") {
        return `Van vrijwilligerslijst: primair type gezet op dominicusganger (was ${String(d.entity_kind_before ?? "—")}, met labels).${autoSuffix}`;
      }
      return `Van vrijwilligerslijst naar dominicusganger (was primair vrijwilliger).${autoSuffix}`;
    }
    case "dominicusganger.promote":
      return `Promotie van dominicusganger naar ${KIND_NL[String(d.entity_kind_after)] ?? String(d.entity_kind_after ?? "—")}.`;
    case "beheer.merge_contacts": {
      const keep = num(d.keep_id);
      const removed = num(d.removed_id);
      if (keep === contactId) {
        return `Samenvoegen: deze kaart (#${keep}) bleef bestaan; kaart #${removed ?? "?"} is erin opgegaan.`;
      }
      if (removed === contactId) {
        return `Samenvoegen: deze kaart (#${removed}) is opgegaan in kaart #${keep ?? "?"}.`;
      }
      return `Samenvoegen van contactkaarten (kaart #${keep ?? "?"}, verwijderd #${removed ?? "?"}).`;
    }
    case "werkgroep_intake.approved":
    case "werkgroep_intake.marked_no_op":
    case "werkgroep_intake.rejected": {
      const gn =
        (typeof d.group_name === "string" && d.group_name.trim() ? d.group_name.trim() : null) ??
        row.group_name?.trim() ??
        "deze werkgroep";
      if (row.action === "werkgroep_intake.approved") {
        return `Werkgroep-ledenlijst voor “${gn}” goedgekeurd en in het adresboek verwerkt.`;
      }
      if (row.action === "werkgroep_intake.marked_no_op") {
        return `Werkgroep-intake voor “${gn}” afgehandeld (geen adresboekwijzigingen nodig).`;
      }
      return `Werkgroep-ledenlijst voor “${gn}” afgekeurd.`;
    }
    default:
      break;
  }

  /** Nieuwe kaart via formulier (POST /api/…/contacts); niet bulk-import (die logt apart). */
  if (row.entity_type === "contact" && row.action.endsWith(".create")) {
    const datum = formatAuditDateLong(row.created_at);
    const k = typeof d.entity_kind === "string" ? KIND_NL[d.entity_kind] ?? d.entity_kind : null;
    const manual = d.source === "manual" || d.created_via === "form";
    if (manual || d.source == null) {
      return k
        ? `Handmatig aangemaakt op ${datum} in de lijst ${k}${nameHint}.`
        : `Handmatig aangemaakt op ${datum}${nameHint}.`;
    }
    return k ? `Kaart aangemaakt op ${datum} (${k})${nameHint}.` : `Kaart aangemaakt op ${datum}${nameHint}.`;
  }

  if (row.action.endsWith(".create")) {
    const k = typeof d.entity_kind === "string" ? KIND_NL[d.entity_kind] ?? d.entity_kind : null;
    return k ? `Kaart aangemaakt in lijst ${k}${nameHint}.` : `Kaart aangemaakt${nameHint}.`;
  }
  if (row.action.endsWith(".update")) {
    return `Gegevens op de kaart bijgewerkt${nameHint}.`;
  }
  if (row.action.endsWith(".delete")) {
    return `Contactkaart verwijderd${nameHint}.`;
  }

  if (row.action === "bezoekersbestand.create") {
    return `Kaart aangemaakt (dominicusganger / bezoekersbestand).`;
  }
  if (row.action === "bezoekersbestand.update") {
    return `Gegevens bijgewerkt (dominicusganger / bezoekersbestand).`;
  }
  if (row.action === "bezoekersbestand.delete") {
    return `Kaart verwijderd uit bezoekersbestand.`;
  }

  return row.action.replace(/\./g, " · ");
}

export function formatAuditWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Alleen datum (lange maandnaam), voor zinnen zoals “handmatig aangemaakt op …”. */
function formatAuditDateLong(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
